// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { isNonEmptyString } from "@insite/client-framework/Common/Utilities/StringUtilities";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCartState,
    getCurrentCartState,
    isCartEmpty,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block";
import ArrowBackward from "@store/Library/Components/Toolkit/Icons/ArrowBackward";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import Typography, { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import { orderProps } from "@store/Themes/common/props/order";
import applyAllowance from "@store/Extensions/Store/Pages/CheckoutReviewAndSubmit/Handlers/ApplyAllowance";
import removeAllowance from "@store/Extensions/Store/Pages/CheckoutReviewAndSubmit/Handlers/RemoveAllowance";
import useBudgetRules from "@store/Framework/Common/Hooks/useBudgetRules";
import { useCartAllowance } from "@store/Framework/Store/Store/Data/Allowance/AllowanceHooks";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { ChangeEvent, FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = getQueryStrings(state);
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const cart = cartState.value;

    const { isApplyingAllowance, isRemovingAllowance, allowanceErrorMessage } = state.pages.checkoutReviewAndSubmit;

    return {
        cart,
        isApplyButtonDisabled: cartState.isLoading || isApplyingAllowance || isCartEmpty(cart),
        isRemoveLinkDisabled: cartState.isLoading || isRemovingAllowance || isCartEmpty(cart),
        allowanceErrorMessage,
    };
};

const mapDispatchToProps = {
    applyAllowance,
    removeAllowance,
};

const enum fields {
    fieldPlaceholderText = "fieldPlaceholderText",
    applyButtonLabel = "applyButtonLabel",
    removeLinkLabel = "removeLinkLabel",
}

export interface CheckoutReviewAndSubmitAllowanceProps extends WidgetProps {
    fields: {
        [fields.fieldPlaceholderText]: string;
        [fields.applyButtonLabel]: string;
        [fields.removeLinkLabel]: string;
    };
}

type Props = CheckoutReviewAndSubmitAllowanceProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutReviewAndSubmitAllowanceStyles {
    block?: BlockProps;
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    textContent?: TypographyPresentationProps;
    allowanceTextField?: TextFieldPresentationProps;
    form?: InjectableCss;
    submitButton?: ButtonPresentationProps;
    removeLink?: LinkPresentationProps;
    errorMessage?: TypographyPresentationProps;
}

export const checkoutReviewAndSubmitAllowanceStyles: CheckoutReviewAndSubmitAllowanceStyles = {
    block: orderProps.block,
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: ${pxToRem(20)};
            background: ${getColor("neutral.light")};
        `,
    },
    title: {
        forwardAs: "h5",
        css: css`
            margin: 0 0 ${pxToRem(10)} 0;
            font-size: ${x => x.theme.design.typography.size.larger};
            font-weight: ${x => x.theme.design.typography.weight.medium};
            line-height: ${x => x.theme.design.typography.lineHeight.small};
        `,
    },
    textContent: {
        variant: "p",
        css: css`
            line-height: ${x => x.theme.design.typography.lineHeight.small};
        `,
    },
    allowanceTextField: {
        mergeCss: true,
        cssOverrides: {
            formInputWrapper: css`
                flex: 1;
            `,
        },
    },
    form: {
        css: css`
            display: flex;
            padding-top: ${pxToRem(14)};
            gap: ${pxToRem(10)};
            max-width: ${pxToRem(285)};
        `,
    },
    submitButton: {
        mergeCss: true,
        css: css`
            min-width: fit-content;
        `,
    },
    removeLink: mergeProps(linkGetActionLinkProps(ArrowBackward), {
        css: css`
            width: fit-content;
        `,
    }),
    errorMessage: {
        forwardAs: "p",
        color: "danger",
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
            line-height: ${x => x.theme.design.typography.lineHeight.small};
            margin: ${pxToRem(12)} 0 0 0;
        `,
    },
};

const styles = checkoutReviewAndSubmitAllowanceStyles;

const CheckoutReviewAndSubmitAllowance: FC<Props> = ({
    cart,
    isApplyButtonDisabled,
    isRemoveLinkDisabled,
    allowanceErrorMessage,
    applyAllowance,
    removeAllowance,
    fields: { fieldPlaceholderText, applyButtonLabel, removeLinkLabel },
}) => {
    const [userAllowance, setUserAllowance] = useState("");
    const {
        hasAllowanceApplied,
        allowanceAppliedDisplay,
        allowanceAvailable,
        hasAllowanceAvailable,
        allowanceAvailableDisplay,
        allowanceRemainingDisplay,
    } = useCartAllowance();
    const { budgetManagementEnabled, hasRoleWithAllowances } = useBudgetRules();

    if (!budgetManagementEnabled || !hasRoleWithAllowances || !cart || isCartEmpty(cart) || !hasAllowanceAvailable) {
        return null;
    }

    const handleAllowanceChange = (event: ChangeEvent<HTMLInputElement>) => setUserAllowance(event.currentTarget.value);

    const applyAllowanceHandler = () => {
        applyAllowance({
            amount: isNonEmptyString(userAllowance) ? userAllowance : "0",
        });
        setUserAllowance("");
    };

    const removeAllowanceHandler = () => {
        removeAllowance();
        setUserAllowance("");
    };

    const keyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter" || event.keyCode === 13) {
            event.preventDefault();
            applyAllowanceHandler();
        }
    };

    return (
        <Block {...styles.block} data-test-selector="checkoutReviewAndSubmit_allowance">
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.title}>
                    {hasAllowanceApplied && allowanceAppliedDisplay
                        ? translate("{0} of your Allowance has been applied to this order.", allowanceAppliedDisplay)
                        : translate("You have {0} Allowance available.", allowanceAvailableDisplay)}
                </Typography>
                <Typography {...styles.textContent}>
                    {hasAllowanceApplied ? (
                        <>{translate("Remaining balance: {0}", allowanceRemainingDisplay)}</>
                    ) : (
                        translate("You can use your Allowance to pay for your order, either in full or partially.")
                    )}
                </Typography>
                {hasAllowanceApplied ? (
                    <Link
                        {...styles.removeLink}
                        onClick={(event: React.MouseEvent<Element, MouseEvent>) => {
                            event.preventDefault();
                            removeAllowanceHandler();
                        }}
                        disabled={isRemoveLinkDisabled}
                        data-test-selector="checkoutReviewAndSubmit_allowanceRemoveLink"
                    >
                        {removeLinkLabel}
                    </Link>
                ) : (
                    <>
                        <StyledWrapper {...styles.form}>
                            <TextField
                                {...styles.allowanceTextField}
                                value={userAllowance}
                                type="number"
                                min={0}
                                max={allowanceAvailable.replace(",", ".")} // allowanceAvailable will never have a comma or a dot at any other position than before decimals
                                step={0.01}
                                placeholder={fieldPlaceholderText}
                                onChange={handleAllowanceChange}
                                onKeyDown={keyDownHandler}
                                error={isNonEmptyString(allowanceErrorMessage)}
                                disabled={false}
                                data-test-selector="checkoutReviewAndSubmit_allowanceTextField"
                            />
                            <Button
                                {...styles.submitButton}
                                type="submit"
                                disabled={isApplyButtonDisabled}
                                onClick={applyAllowanceHandler}
                                data-test-selector="checkoutReviewAndSubmit_allowanceApplyButton"
                            >
                                {applyButtonLabel}
                            </Button>
                        </StyledWrapper>
                        {isNonEmptyString(allowanceErrorMessage) && (
                            <Typography {...styles.errorMessage}>{allowanceErrorMessage}</Typography>
                        )}
                    </>
                )}
            </StyledWrapper>
        </Block>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndSubmitAllowance),
    definition: {
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
        displayName: "Allowance",
        fieldDefinitions: [
            {
                name: fields.fieldPlaceholderText,
                displayName: "Field Placeholder text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Enter amount",
            },
            {
                name: fields.applyButtonLabel,
                displayName: "Apply Button Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Apply",
            },
            {
                name: fields.removeLinkLabel,
                displayName: "Remove Link Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Remove",
            },
        ],
    },
};

export default widgetModule;
